import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { type Locale, type Translations, getTranslations, LOCALE_NAMES, LOCALE_FLAGS } from "@/lib/translations";

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const isServer = typeof window === "undefined";

function detectBrowserLocale(): Locale {
  if (isServer) return "en";
  const lang = navigator.language?.toLowerCase() || "en";
  if (lang.startsWith("de")) return "de";
  if (lang.startsWith("es")) return "es";
  if (lang.startsWith("it")) return "it";
  if (lang.startsWith("fr")) return "fr";
  return "en";
}

function getLocaleFromPath(pathname?: string): Locale | null {
  // If an explicit pathname is provided (from wouter's Router context), use it
  if (pathname) {
    const path = pathname.split("/")[1];
    if (["de", "es", "it", "fr"].includes(path)) return path as Locale;
    return null;
  }
  if (isServer) return null;
  const path = window.location.pathname.split("/")[1];
  if (["de", "es", "it", "fr"].includes(path)) return path as Locale;
  return null;
}

interface LanguageProviderProps {
  children: ReactNode;
  /** Server-side: pass the request URL pathname to detect locale without window */
  ssrPathname?: string;
}

export function LanguageProvider({ children, ssrPathname }: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    // Priority: URL path > browser detection
    // Note: We use ssrPathname (from wouter Router context) for both server and client
    // to ensure hydration match. localStorage is applied post-mount via useEffect.
    const pathLocale = getLocaleFromPath(ssrPathname);
    if (pathLocale) return pathLocale;
    if (!isServer) {
      return detectBrowserLocale();
    }
    return "en";
  });

  // Apply localStorage preference after mount (avoids hydration mismatch)
  useEffect(() => {
    const stored = localStorage.getItem("nokuhiro-locale") as Locale;
    if (stored && ["en", "de", "es", "it", "fr"].includes(stored)) {
      // Only apply if no locale in URL path (URL takes priority)
      const pathLocale = getLocaleFromPath(window.location.pathname);
      if (!pathLocale && stored !== locale) {
        setLocaleState(stored);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    if (isServer) return;
    localStorage.setItem("nokuhiro-locale", newLocale);
    // Update URL path
    const currentPath = window.location.pathname;
    const segments = currentPath.split("/").filter(Boolean);
    const locales = ["de", "es", "it", "fr"];
    if (locales.includes(segments[0])) {
      segments.shift();
    }
    if (newLocale === "en") {
      window.history.replaceState(null, "", "/" + segments.join("/"));
    } else {
      window.history.replaceState(null, "", "/" + newLocale + (segments.length ? "/" + segments.join("/") : ""));
    }
    // Update html lang attribute
    document.documentElement.lang = newLocale;
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = getTranslations(locale);

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}

export { LOCALE_NAMES, LOCALE_FLAGS };
export type { Locale };
